import { graphql } from '@api/generated';
import { useLazyQuery } from '@apollo/client/react';
import { SearchJobQuery } from '@api/generated/graphql';

/** Page of tenant jobs returned by the `searchJob` query. */
export type Jobs = NonNullable<SearchJobQuery['jobs']>;
/** Single tenant-job record (the shape consumers receive on confirm). */
export type JobResult = NonNullable<Jobs['items']>[0];

export const SEARCH_JOB_QUERY = graphql(`
  query searchJob($tenantId: ID!, $jobNumber: String) {
    jobs(tenantId: $tenantId, jobNumber: $jobNumber, skip: null, take: 10) {
      items {
        id
        jobType
        tenantId
        createdAt
        leadCallId
        customer {
          name
        }
        jobLocation {
          address
        }
      }
    }
  }
`);

/**
 * Hook that exposes a lazy job-number lookup against the tenant-scoped `jobs`
 * query and normalizes the first matching item (or `null`) as `jobResult`.
 *
 * `network-only` is used so the picker never returns stale results when the
 * agent re-searches after a typo.
 */
export const useJobLookup = () => {
  const [refetchSearchJob, { data, loading, error }] = useLazyQuery(
    SEARCH_JOB_QUERY,
    {
      fetchPolicy: 'network-only',
    },
  );

  return {
    jobResult: data?.jobs?.items?.length ? data.jobs.items[0] : null,
    loading,
    error,
    refetchSearchJob,
  };
};
